import hexRgb from 'hex-rgb';
import rgbHex from 'rgb-hex';
import convert from 'color-convert';
import { withBounds } from '@clover-util/with-bounds';
import { adjustPercentage } from './adjustPercentage';

export type RawColor = string | readonly [ number, number, number ] | readonly [ number, number, number, number ];

export function tryParseRawColor(color: RawColor) {
  try {
    return typeof color !== 'string' ? color : hexRgb(color.trim(), { format: 'array' });
  } catch(e) {
    console.error(`Invalid Hex Value: "${color}"`, e);
    return [1, 1, 1] as const;
  }
}

export type Color = Readonly<{
  /**
   * The color as a color css hex string
   */
  hex: string;

  /**
   * The color as a css rgb or rgba string
   */
  rgb: string;

  /**
   * Create an alpha variant on the fly
   * @param alpha a value from 0 to 1
   */
  alpha(alpha?: number): Color;

  /**
   * Create a lighter or darker color via lightness in the HSL model
   * @param delta a value from -1 to 1 (percentage darker or lighter)
   */
  lightness(delta?: number): Color;

  /**
   * Create a lighter or darker color via value or brightness in the HSV / HSB model
   * @param delta a value from -1 to 1 (percentage darker or lighter)
   */
  brightness(delta?: number): Color;

  /**
   * Create a lighter or darker color via whiteness in the HWB model
   * @param delta a value from -1 to 1 (percentage darker or lighter)
   */
  whiteness(delta?: number): Color;

  /**
   * Create a lighter or darker color via blackness in the HWB model
   * @param delta a value from -1 to 1 (percentage darker or lighter)
   */
  blackness(delta?: number): Color;
}>;

function createColor(r: number, g: number, b: number, a: number): Color {
  const hex = `#${rgbHex(r, g, b)}`;

  const rgb =  hexRgb(a === 1 ? hex : rgbHex(r, g, b, a), { format: 'css' });

  return {
    hex,
    rgb,
    alpha: (alpha = a) => createColor(r, g, b, withBounds(alpha, { upper: 1, lower: 0 })),
    lightness(delta = 0) {
      const [h, s, l] = convert.hex.hsl.raw(hex);

      return createColor(...convert.hsl.rgb([ h, s, adjustPercentage(l, delta) ]), a);
    },
    brightness(delta = 0) {
      const [h, s, v] = convert.hex.hsv.raw(hex);

      return createColor(...convert.hsv.rgb([ h, s, adjustPercentage(v, delta) ]), a);
    },
    whiteness(delta = 0) {
      const [hue, whiteness, blackness] = convert.hex.hwb.raw(hex);

      return createColor(...convert.hwb.rgb([ hue, adjustPercentage(whiteness, delta), blackness ]), a);
    },
    blackness(delta = 0) {
      const [hue, whiteness, blackness] = convert.hex.hwb.raw(hex);

      return createColor(...convert.hwb.rgb([ hue, whiteness, adjustPercentage(blackness, delta) ]), a);
     },
  };
}

export type Swatch<T> = Color & Readonly<{[P in keyof T]: Color }>;

/**
 * Parses a hex color string or rgb(a) as a tuple
 * @param color the color (hex or tuple of numbers)
 * @param variants options
 * @returns a swatch
 */
export function createSwatch<T extends Record<string, RawColor>>(color: RawColor, variants = {} as T): Swatch<T> {
  const [r, g, b, a = 1] = tryParseRawColor(color);

  return {
    ...Object.entries(variants).map(([key, variant]) => ({
      [key]: createSwatch(variant),
    }) as Record<string, Color>).reduce(Object.assign, {}),
    ...createColor(r, g, b, a),
  } as Swatch<T>;
}
